/**
 * Presentational toast item. Auto-dismiss is handled by the ToastProvider (see
 * toastContext.tsx); this component only renders one toast and its close button.
 */
import type { ReactElement } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  type: ToastType;
  message: string;
  onClose: () => void;
}

const styleMap: Record<ToastType, string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  warning: 'bg-yellow-500 text-white',
  info: 'bg-brand-600 text-white',
};

/** A single toast notification card. */
export default function Toast({ type, message, onClose }: ToastProps): ReactElement {
  const isAssertive = type === 'error' || type === 'warning';
  return (
    <div
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
      className={`pointer-events-auto flex items-start gap-3 rounded-md px-4 py-3 text-sm shadow-lg ${styleMap[type]}`}
    >
      <p className="min-w-0 flex-1 break-words">{message}</p>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded p-0.5 opacity-80 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/60"
        aria-label="Đóng"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
